<template>
  <BContainer fluid class="messenger-container">
    <BRow class="h-100">
      <!-- Список чатов -->
      <BCol
        cols="12"
        lg="4"
        class="chat-list-column border-end"
        :class="{ 'd-none': isMobile && showMessageArea }"
      >
        <ChatList
          :chats="chats"
          :current-user="currentUser"
          :selected-chat-id="selectedChatId"
          @select-chat="handleChatSelect"
        />
      </BCol>

      <!-- Область сообщений -->
      <BCol
        cols="12"
        lg="8"
        class="messages-column"
        :class="{ 'd-none': isMobile && !showMessageArea }"
      >
        <MessageArea
          v-if="selectedChat"
          :chat-title="selectedChat.name"
          :messages="selectedChatMessages"
          :current-user="currentUser"
          :is-mobile="isMobile"
          @close-chat="handleCloseChat"
        />

        <MessageInput
          v-if="selectedChat"
          @send-message="handleSendMessage"
        />

        <div v-else class="d-flex align-items-center justify-content-center h-100 text-muted">
          <div class="text-center">
            <p class="mt-3">Выберите чат для начала общения</p>
          </div>
        </div>
      </BCol>
    </BRow>
  </BContainer>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { BContainer, BRow, BCol, useToast } from 'bootstrap-vue-next'
import ChatList from './ChatList.vue'
import MessageArea from './MessageArea.vue'
import MessageInput from './MessageInput.vue'

export default {
  name: 'Messenger',
  components: {
    BContainer,
    BRow,
    BCol,
    ChatList,
    MessageArea,
    MessageInput,
  },
  props: {
    currentUser: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const apiUrl = process.env.VUE_APP_BACKEND_URL;
    const apiKey = process.env.VUE_APP_API_KEY;

    const {create} = useToast()

    const chats = ref([])
    const selectedChatId = ref(null)
    const selectedChatMessages = ref([])

    const maxUpdateTimeout = 60000
    const minUpdateTimeout = 10000
    const stepUpdateTimeout = 10000

    const showMessageArea = ref(false)
    const isMobile = ref(false)

    let updateTimeout = maxUpdateTimeout / 2
    let needUpdateMessages = false
    let updatingHasStarted = false
    let skippedUpdateCycles = 0

    // определение мобильного устройства
    const checkMobile = () => {
      console.log(window.innerWidth)
      console.log(window.innerWidth < 992)
      isMobile.value = window.innerWidth < 992 // Bootstrap lg breakpoint
    }

    // запуска таймера обновления чатов
    const autoUpdateChats = () => {
      setInterval(async () => {
        let updated = false

        console.log('timeout: '+updateTimeout)

        // проверка/включение блокировки обновления чатов
        if (updatingHasStarted) {
          skippedUpdateCycles++
          if (skippedUpdateCycles < 10) return
        }
        updatingHasStarted = true
        skippedUpdateCycles = 0


        // обновление чатов и сообщений в открытом чате
        await updateChats()
        if (selectedChatId.value && needUpdateMessages) {
          updated = await updateMessages(selectedChatId.value)
          if (updated) await updateMessagesStatusForCurrentChat(selectedChatId.value)
        }

        // управление таймаутом обновления сообщений
        if (updated) {
          updateTimeout = minUpdateTimeout
        } else {
          if (updateTimeout < maxUpdateTimeout) updateTimeout += stepUpdateTimeout
        }

        // снятие блокировки обновления чатов
        updatingHasStarted = false
      }, updateTimeout)
    }

    // выбранный чат (из загруженных)
    const selectedChat = computed(() => {
      return chats.value.find(chat => chat.id === selectedChatId.value) || null
    })

    // получить чат по его id (из загруженных)
    const getChatById = (id) => {
      return chats.value.find(chat => chat.id === id) || null
    }

    // получить сообщение по его id (из загруженных)
    const getMessageById = (id) => {
      return selectedChatMessages.value.find(msg => msg.id === id) || null
    }

    const formatDateTime = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    // подгрузить сообщения чата
    const handleChatSelect = async (chatId) => {
      selectedChatId.value = chatId
      showMessageArea.value = true

      try {
        const data = await _getMessages(chatId)

        selectedChatMessages.value = []

        for (let item of data) {
          addMessageToSelectedChat(item)
        }
      } catch (error) {
        console.error(error)
        selectedChatMessages.value = []

        create({
          title: error,
          variant: 'danger'
        })
      }

      await updateMessagesStatusForCurrentChat(chatId)
    }

    const handleCloseChat = () => {
      showMessageArea.value = false
    }

    const recognizeLink = (safeText) => {
      const urlRegex = /(\b(https?|ftp):\/\/[^\s<>"']*[^\s<>"'.,;?!])/gi
      const emailRegex = /(\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b)/g

      safeText = safeText.replace(urlRegex, function(url) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      })

      safeText = safeText.replace(emailRegex, function(email) {
        return `<a href="mailto:${email}">${email}</a>`
      })

      return safeText
    }

    // добавить сообщение в активный (открытый в данный момент) чат
    const addMessageToSelectedChat = (item) => {
      const msgItem = {
        id: item.id,
        message_tg_id: item.message_tg_id,
        reply_to_tg_id: item.reply_to_tg_id,
        reply_message: item.reply_message,
        text: recognizeLink(item.message),
        senderId: item.user_id,
        senderName: item.user_name,
        timestamp: Math.floor(new Date(item.date).getTime() / 1000),
        isRead: item.status == 2 ? true : false,
      }
      selectedChatMessages.value.push(msgItem)

      return msgItem
    }

    // обновить статусы входящих сообщений на просмотренные для активного чата
    const updateMessagesStatusForCurrentChat = async (chatId) => {
      const success = await updateMessagesStatus(chatId)
      if (success) selectedChat.value.unreadCount = 0

      return success
    }

    // обновить статусы входящих сообщений на просмотренные
    const updateMessagesStatus = async (chatId) => {
      let success = false

      try {
        const response = await fetch(apiUrl+'/msg/messages/status?chat_id='+chatId+'&user_id='+props.currentUser.id, {
          headers: {
            'API-KEY': apiKey,
          }
        })
        if (!response.ok) {
          throw new Error('Ошибка обновления статусов сообщений')
        }

        const data = await response.json()
        console.log(data)

        success = true
      } catch (error) {
        console.error(error)
      }

      return success
    }

    // обработчик отправки сообщения
    const handleSendMessage = async (messageText) => {
      if (!selectedChat.value || !selectedChatId.value || !messageText.trim()) return

      let messageId = null
      let messageTgId = null
      const d = new Date()
      const item = {
        message: messageText.trim(),
        user_id: props.currentUser.id,
        group_id: selectedChatId.value,
        date: formatDateTime(d),
        status: 1,
      }

      // отправка сообщения, получение id
      try {
        const response = await fetch(apiUrl+'/msg/message/send', {
          method: 'POST',
          headers: {
            'API-KEY': apiKey,
            'Content-Type': 'application/json;charset=utf-8'
          },
          body: JSON.stringify(item)
        })
        if (!response.ok) {
          throw new Error('Ошибка отправки сообщения')
        }

        const data = await response.json()
        console.log(data)

        if (typeof data.last_insert_id == 'undefined') throw new Error('Сообщение не доставлено')
        messageId = data.last_insert_id
        messageTgId = data.message_tg_id
      } catch (error) {
        console.error(error)

        create({
          title: error,
          variant: 'danger'
        })
      }

      if (!messageId) return

      // Добавляем сообщение в выбранный чат
      item['id'] = messageId
      item['message_tg_id'] = messageTgId
      item['user_name'] = props.currentUser.name
      const newMessage = addMessageToSelectedChat(item)
      const chatIndex = chats.value.findIndex(chat => chat.id === selectedChatId.value)
      if (chatIndex !== -1) {
        chats.value[chatIndex].lastMessage = item.message
        chats.value[chatIndex].lastMessageTime = newMessage.timestamp
      }
    }

    // protected - получить чаты с сервера
    const _getChats = async () => {
      const response = await fetch(apiUrl+'/msg/chats?user_id='+props.currentUser.id, {
        headers: {
          'API-KEY': apiKey,
        }
      })
      if (!response.ok) {
        throw new Error('Ошибка загрузки чатов')
      }

      const data = await response.json()
      console.log(data)

      return data
    }

    // protected - получить сообщения с сервера
    const _getMessages = async (chatId) => {
      const response = await fetch(apiUrl+'/msg/messages?chat_id='+chatId, {
        headers: {
          'API-KEY': apiKey,
        }
      })
      if (!response.ok) {
        throw new Error('Ошибка загрузки сообщений')
      }

      const data = await response.json()
      console.log(data)

      return data
    }

    // первичная загрузка чатов с сервера
    const loadChats = async () => {
      try {
        const data = await _getChats()

        for (let item of data) {
          const groupItem = {
            id: item.group_id,
            name: item.group_name,
            avatar: null,
            lastMessage: item.last_message,
            lastMessageTime: Math.floor(new Date(item.last_message_date).getTime() / 1000),
            unreadCount: item.unread_count,
            messages: [],
          }
          chats.value.push(groupItem)
        }

        autoUpdateChats()
      } catch (error) {
        console.error(error)
        chats.value = []

        create({
          title: error,
          variant: 'danger'
        })
      }
    }

    // обновить чаты
    const updateChats = async () => {
      try {
        const data = await _getChats()

        for (let item of data) {
          const loadedChat = getChatById(item.group_id)
          const newLastMessageTime = Math.floor(new Date(item.last_message_date).getTime() / 1000)

          if (loadedChat.lastMessageTime != newLastMessageTime) {
            // если обновился открытый чат, то поднять флаг необходимости обновления сообщений в нем
            if (selectedChatId.value == item.group_id) {
              needUpdateMessages = true
            } else {
              loadedChat.unreadCount = item.unread_count
            }

            loadedChat.lastMessage = item.last_message
            loadedChat.lastMessageTime = newLastMessageTime
          }
        }
      } catch (error) {
        console.error(error)
      }
    }

    // обновить сообщения
    const updateMessages = async (chatId) => {
      let updated = false

      try {
        const data = await _getMessages(chatId)

        for (let item of data) {
          // если сообщение отсутствует
          if (!getMessageById(item.id)) {
            addMessageToSelectedChat(item)
            updated = true
          }
        }

        needUpdateMessages = false
      } catch (error) {
        console.error(error)
      }

      return updated
    }

    onMounted(() => {
      checkMobile()
      loadChats()
    })

    return {
      chats,
      selectedChatId,
      selectedChat,
      selectedChatMessages,
      showMessageArea,
      isMobile,
      handleChatSelect,
      handleSendMessage,
      handleCloseChat,
    }
  }
}
</script>

<style scoped>
.messenger-container {
  height: 100vh;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.chat-list-column {
  background-color: #f8f9fa;
  padding: 0;
}

.messages-column {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  max-height: 100%;
  position: relative;
}
</style>
